import React, { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonials, businessInfo } from "../data/mockData";

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);
  const itemsPerPage = 3;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const maxIndex = Math.max(0, testimonials.length - itemsPerPage);
  const next = () => setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  const prev = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <section id="testimonials" ref={sectionRef} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-brand-mint-dark text-xs font-bold uppercase tracking-[0.25em] mb-4">
            Testimonials
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl text-gray-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-gray-500 text-[15px] max-w-xl mx-auto">
            Don’t just take our word for it — here’s what our satisfied customers have to say
          </p>
        </div>

        {/* Testimonial Cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {visibleTestimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-gray-50/80 rounded-xl p-7 border border-gray-100/80 transition-all duration-250 hover:shadow-md hover:-translate-y-0.5 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <p className="text-gray-500 text-sm leading-[1.8] mb-6">
                “{testimonial.text}”
              </p>

              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${
                      i < testimonial.rating
                        ? "text-brand-mint-dark fill-brand-mint-dark"
                        : "text-gray-200"
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-brand-charcoal flex items-center justify-center">
                  <span className="text-brand-gold font-bold text-xs">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {testimonial.project} · {testimonial.timeAgo}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation + Stats */}
        <div className="flex flex-col sm:flex-row items-center justify-between">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <button
              onClick={prev}
              disabled={currentIndex === 0}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-brand-charcoal hover:border-brand-charcoal disabled:opacity-25 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-400 font-medium">
              {currentIndex + 1}–{Math.min(currentIndex + itemsPerPage, testimonials.length)} of {testimonials.length}
            </span>
            <button
              onClick={next}
              disabled={currentIndex >= maxIndex}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-brand-charcoal hover:border-brand-charcoal disabled:opacity-25 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <a
            href="https://www.google.com/search?q=KOLD+AIR+LTD+Cottingham+Reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs font-bold text-brand-mint-dark hover:text-brand-charcoal transition-colors tracking-wide"
          >
            <span className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 text-brand-mint-dark fill-brand-mint-dark" />
              ))}
            </span>
            {businessInfo.rating} · {businessInfo.reviewCount}+ Google Reviews
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
